import React, {FC, useEffect, useRef, useState} from 'react'
import AlertMessage from '../../../common/alert/AlertMsg'
import SharedData from '../../../common/SharedData'
import {BulkImagesZip, enqueueBulkAdd, uploadImagesWithBulk} from '../core/_requests'
import {
  SINGLE_REQUEST_MAX_BYTES,
  chunkedUploadUnavailable,
  uploadFailureMessage,
  uploadFailureStatus,
  uploadZipViaTus,
} from '../core/tusUpload'
import {BulkOfferSeed} from '../core/extractBarcodes'
import {ICatalogVariantPreview} from '../../../offers/cors/_models'
import Loader from '../../../common/Loader'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'

/** One ambiguous (show_card) barcode the async ADD worker parked for the
 *  supplier to resolve on the Bulk Jobs review page. */
export type PendingVariantGroup = {
  barcode: string
  variants: ICatalogVariantPreview[]
  seed?: BulkOfferSeed
  /** Backend-tagged parked-row kind: `edit` groups resolve by updating the
   *  owned variant's offer; `add` (default) groups create offers. */
  action?: 'add' | 'edit'
}

type IProps = {
  bulkFile: any
  setBulkFile: React.Dispatch<any>
  imagesFile: any
  setImagesFile: React.Dispatch<any>
}

// Distinct phases so the UI can show a real progress bar (determinate) for the
// resumable image upload vs. an indeterminate spinner for the quick sheet enqueue.
type UploadPhase = 'idle' | 'hashing' | 'uploading' | 'reconnecting' | 'submitting'

const SubmitButtonRow: FC<IProps> = ({bulkFile, setBulkFile, imagesFile, setImagesFile}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const navigate = useNavigate()

  const [phase, setPhase] = useState<UploadPhase>('idle')
  const [percent, setPercent] = useState(0)
  const [error, setError] = useState(false)
  // The real reason when it is known (too large, logged out, connection lost…), else the generic one.
  const [errorMessage, setErrorMessage] = useState('')
  const stopRef = useRef<AbortController | null>(null)
  // Leaving the page stops the ZIP upload, and an answer that arrives afterwards moves no one.
  const unmountedRef = useRef(false)
  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      stopRef.current?.abort()
    }
  }, [])

  const isBusy = phase !== 'idle'

  const isFormHasError = () => {
    let hasError = false
    setBulkFile({...bulkFile, error: false})
    setImagesFile({...imagesFile, error: false})
    if (!bulkFile.value && !imagesFile.value) {
      setBulkFile({...bulkFile, error: true})
      setImagesFile({...imagesFile, error: true})
      hasError = true
    }
    return hasError
  }

  // Upload the images ZIP (if any) via the resumable tus protocol and return its
  // server-side key. A >100 MB ZIP is split into <100 MB chunks so it crosses
  // Cloudflare's request-body cap; a dropped connection resumes from the last
  // completed chunk rather than restarting. Against an API that cannot take the
  // ZIP in pieces yet, a small one is returned as is, to be sent the old way.
  const uploadImagesIfAny = async (): Promise<BulkImagesZip | undefined> => {
    if (!imagesFile.value) {
      return undefined
    }
    setPhase('hashing')
    setPercent(0)
    stopRef.current = new AbortController()
    try {
      return await uploadZipViaTus(imagesFile.value, currentUser?.token, {
        signal: stopRef.current.signal,
        onHashProgress: (p) => setPercent(p),
        onProgress: ({percent}) => {
          setPhase('uploading')
          setPercent(percent)
        },
        onRetry: () => setPhase('reconnecting'),
      })
    } catch (exp: any) {
      if (
        exp?.name !== 'AbortError' &&
        chunkedUploadUnavailable(exp) &&
        imagesFile.value.size <= SINGLE_REQUEST_MAX_BYTES
      ) {
        return imagesFile.value as File
      }
      throw exp
    }
  }

  // Why an upload failed, from the HTTP status (0: no answer at all) and the server's own message.
  const failureText = (status: number, serverMessage: string): string => {
    if (!status) return formatMessage({id: 'upload_error_network'})
    if (status === 413) return formatMessage({id: 'upload_error_too_large'})
    if (status === 401 || status === 403) return formatMessage({id: 'upload_error_session'})
    if (status === 460) return formatMessage({id: 'upload_error_corrupt'})
    if (status === 504 || status === 522 || status === 524) {
      return formatMessage({id: 'upload_error_timeout'}, {status})
    }
    if (serverMessage) return formatMessage({id: 'upload_error_server'}, {message: serverMessage})
    return formatMessage({id: 'upload_error_http'}, {status})
  }

  const describeFailure = (exp: any): string => {
    if (exp?.response) {
      // axios: the sheet / images request after the ZIP was sent.
      const data = exp.response.data
      return failureText(exp.response.status, String(data?.data?.message || data?.message || ''))
    }
    if (exp?.originalRequest || exp?.originalResponse) {
      // tus-js-client: one of the ZIP's pieces.
      return failureText(uploadFailureStatus(exp), uploadFailureMessage(exp))
    }
    return exp?.request ? failureText(0, '') : ''
  }

  const handleSubmit = async () => {
    if (isFormHasError()) return
    setError(false)
    setErrorMessage('')
    try {
      const images = await uploadImagesIfAny()
      if (unmountedRef.current) return
      setPhase('submitting')

      if (bulkFile.value) {
        // Async: the sheet (+ optional image-zip key) are queued and processed in
        // the background (new-product requests, zip attach, auto-listed offers,
        // parked ambiguous variants). Progress + resolution happen on Bulk Jobs.
        const res: any = await enqueueBulkAdd(currentUser?.token, bulkFile.value, images, language)
        if (unmountedRef.current) return
        if (res?.data?.job_id) {
          navigate('/products/bulk-jobs')
        } else {
          setError(true)
          setPhase('idle')
        }
      } else if (images) {
        // Only a zip (no sheet): attach images to already-filed requests. A big ZIP takes longer
        // to attach than Cloudflare waits for an answer, so the API queues it as a Bulk Job.
        const res: any = await uploadImagesWithBulk(images, currentUser?.token)
        if (unmountedRef.current) return
        if (res?.data?.data?.job_id || res?.data?.job_id) {
          navigate('/products/bulk-jobs')
        } else {
          localStorage.setItem('thankYouPageURL', window.location.href)
          navigate('/thank-you?go=request-update-products')
        }
      } else {
        setPhase('idle')
      }
    } catch (exp: any) {
      if (unmountedRef.current) return
      setPhase('idle')
      if (exp?.name === 'AbortError') {
        return
      }
      setErrorMessage(describeFailure(exp))
      setError(true)
    } finally {
      stopRef.current = null
    }
  }

  const isUploadingZip = phase === 'hashing' || phase === 'uploading' || phase === 'reconnecting'

  const phaseLabel =
    phase === 'reconnecting'
      ? formatMessage({id: 'upload_reconnecting'})
      : phase === 'hashing'
      ? formatMessage({id: 'upload_hashing'})
      : formatMessage({id: 'uploading_images'})

  // Determinate bar while the image ZIP uploads; the quick sheet-enqueue step
  // ('submitting') keeps the existing full-screen spinner.
  const showProgress = isUploadingZip

  return (
    <>
      <div className='apb-actionbar'>
        <div className='apb-actionbar-note'>
          <i className='fas fa-shield-halved' aria-hidden='true' />
          <span>{formatMessage({id: 'by_uploading_products_stnrd'})}</span>
        </div>
        <div className='apb-actionbar-btns'>
          <button
            type='button'
            className='apb-btn-cancel'
            onClick={() => (isUploadingZip ? stopRef.current?.abort() : navigate(-1))}
            disabled={isBusy && !isUploadingZip}
          >
            {formatMessage({id: isUploadingZip ? 'upload_stop' : 'cancel'})}
          </button>
          <button type='button' onClick={handleSubmit} className='apb-btn-upload' disabled={isBusy}>
            {formatMessage({id: 'upload'})}
          </button>
        </div>
      </div>

      {showProgress && (
        <div className='apb-upload-progress' data-testid='tus-progress'>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <span className={phase === 'reconnecting' ? 'text-warning' : ''}>{phaseLabel}</span>
            <span>{percent}%</span>
          </div>
          <div className='progress' style={{height: '8px'}}>
            <div
              className={
                'progress-bar ' +
                (phase === 'reconnecting'
                  ? 'bg-warning progress-bar-striped progress-bar-animated'
                  : 'bg-danger')
              }
              role='progressbar'
              style={{width: `${percent}%`}}
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      )}

      {phase === 'submitting' && <Loader isFull={true} />}

      {error === true && (
        <AlertMessage
          title={formatMessage({id: 'something_wrong_title'})}
          message={errorMessage || formatMessage({id: 'something_wrong_desc'})}
          onClose={() => setError(false)}
        />
      )}
    </>
  )
}

export default SubmitButtonRow
